import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhoneAlt, FaCoffee, FaLinkedin, FaGithub } from 'react-icons/fa';
import { RiTwitterXFill } from 'react-icons/ri';
import { FaInstagram } from 'react-icons/fa';
import { portfolioData } from '../data/portfolioData';
import './ContactMe.css';

const ContactMe: React.FC = () => {
  const { contactInfo } = portfolioData;
  const [activeSocial, setActiveSocial] = useState<string | null>(null);
  const [showPhonePuzzle, setShowPhonePuzzle] = useState(false);
  const [puzzleAnswer, setPuzzleAnswer] = useState('');
  const [puzzleSolved, setPuzzleSolved] = useState(false);
  const [solvedDigits, setSolvedDigits] = useState('');

  // Format the phone number to show with hidden digits
  const formattedPhone = contactInfo.phoneNumber + "??";
  
  // Obfuscated validation - compares transformed input
  const validateAnswer = (input: string): boolean => {
    const a = input.charCodeAt(0) || 0;
    const b = input.charCodeAt(1) || 0;
    return a * 7 + b * 13 === 1106 && a - b === -2;
  };

  // Check if puzzle is solved correctly
  const checkPuzzleAnswer = () => {
    if (validateAnswer(puzzleAnswer)) {
      alert('Correct! Let\'s connect.');
      setSolvedDigits(puzzleAnswer);
      setPuzzleSolved(true);
      
      // Close the modal automatically upon correct answer
      setTimeout(() => {
        setShowPhonePuzzle(false);
      }, 1500); // Slight delay to allow user to see the full number
    } else {
      setPuzzleAnswer('');
      alert('Not quite right! Try again.');
    }
  };

  const socialLinks = [
    { id: 'linkedin', icon: <FaLinkedin />, url: contactInfo.linkedinLink, label: 'LinkedIn' },
    { id: 'github', icon: <FaGithub />, url: 'https://github.com/Anyaoha', label: 'GitHub' },
    { id: 'twitter', icon: <RiTwitterXFill />, url: 'https://twitter.com/sinewslink', label: 'X' },
    { id: 'instagram', icon: <FaInstagram />, url: 'https://www.instagram.com/sinewslink', label: 'Instagram' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  return (
    <motion.div 
      className="connect-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div className="connect-hero" variants={itemVariants}>
        <div className="connect-overlay"></div>
        <motion.img 
          src="/images/anyaoha.gif" 
          alt={contactInfo.name} 
          className="connect-avatar"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
        />
        <motion.div 
          className="connect-intro"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h1>{contactInfo.name}</h1>
          <h2>{contactInfo.title}</h2>
          <p>{contactInfo.summary}</p>
        </motion.div>
      </motion.div>

      <motion.div className="connect-content" variants={itemVariants}>
        <motion.h3 
          className="connect-section-title"
          variants={itemVariants}
        >
          Let's Connect
        </motion.h3>
        
        <motion.div className="connect-methods" variants={itemVariants}>
          <motion.a 
            href={`mailto:${contactInfo.email}`}
            className="connect-method email"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            variants={itemVariants}
          >
            <FaEnvelope className="connect-icon" />
            <div className="connect-method-content">
              <h4>Email Me</h4>
              <p>Send a digital message <span className="email-emoji">✉️</span></p>
            </div>
            <div className="connect-glow"></div>
          </motion.a>
          
          <motion.div 
            className="connect-method phone"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            variants={itemVariants}
            onClick={() => setShowPhonePuzzle(true)}
          >
            <FaPhoneAlt className="connect-icon" />
            <div className="connect-method-content">
              <h4>Phone</h4>
              {puzzleSolved ? (
                <p>{contactInfo.phoneNumber + solvedDigits}</p>
              ) : (
                <>
                  <p>{formattedPhone}</p>
                  <span className="phone-hint">Solve the puzzle to reveal</span>
                </>
              )}
            </div>
            <div className="connect-glow"></div>
          </motion.div>
          
          <motion.a 
            href="https://cal.com/anyaoha/tell-me-more"
            target="_blank"
            rel="noopener noreferrer"
            className="connect-method coffee"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            variants={itemVariants}
          >
            <FaCoffee className="connect-icon" />
            <div className="connect-method-content">
              <h4>Coffee Chat</h4>
              <p>Let's grab a virtual coffee</p>
            </div>
            <div className="connect-glow"></div>
          </motion.a>
        </motion.div>
        
        {/* Phone Puzzle Modal */}
        {showPhonePuzzle && (
          <div className="puzzle-modal-overlay" onClick={() => setShowPhonePuzzle(false)}>
            <div className="puzzle-modal" onClick={e => e.stopPropagation()}>
              <h3>Wisdom Puzzle</h3>
              <p>Solve this puzzle to reveal the full phone number:</p>
              <div className="puzzle-question">
                <p>"Eke, Orie, Afọ, Nkwọ — the four faces of time. When the tortoise climbs three sacred hills of the ancestors, each older than the last, and the spider weaves eight threads under the moon's third phase, what number does the earth whisper to the sky? Only the one who hears the language of the ogụ and the spider's dance may speak it."</p>
              </div>
              <div className="puzzle-input">
                <input 
                  type="text" 
                  value={puzzleAnswer} 
                  onChange={(e) => setPuzzleAnswer(e.target.value)} 
                  placeholder="Answer" 
                  maxLength={2}
                />
                <button onClick={checkPuzzleAnswer}>Submit</button>
              </div>
              <button className="close-modal" onClick={() => setShowPhonePuzzle(false)}>Close</button>
            </div>
          </div>
        )}
        
        <motion.h3 
          className="connect-section-title"
          variants={itemVariants}
        >
          Social Profiles
        </motion.h3>
        
        <motion.div 
          className="connect-social"
          variants={itemVariants}
        >
          {socialLinks.map((social) => (
            <motion.a
              key={social.id}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`social-icon ${activeSocial === social.id ? 'active' : ''}`}
              onMouseEnter={() => setActiveSocial(social.id)}
              onMouseLeave={() => setActiveSocial(null)}
              whileHover={{ 
                scale: 1.2,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.9 }}
            >
              {social.icon}
              <span className="social-label">{social.label}</span>
            </motion.a>
          ))}
        </motion.div>
      </motion.div>
      
      <motion.div 
        className="connect-cta"
        variants={itemVariants}
      >
        <p>Looking forward to connecting with you!</p>
        <div className="pulse-container">
          <div className="pulse-circle"></div>
          <div className="pulse-circle"></div>
          <div className="pulse-circle"></div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContactMe;
